/* eslint-disable no-await-in-loop */
/* eslint-disable class-methods-use-this */
/* eslint-disable quotes */
/**
 * Commission service class for interacting with the server-side API
 * Implements the commission process flow with independent operations
 */
class CommissionService {
  /**
   * Service constructor
   * @param {string} [baseURL=process.env.REACT_APP_API_URL] - Base URL for
   * the server-side API
   * @property {string} baseURL - The base URL for the server-side API
   */
  constructor(baseURL = process.env.REACT_APP_API_URL) {
    this.baseURL = baseURL;
    this.endpoints = {
      // Export processes
      fibreCommsReport: "api/commissions/fibre-comms-report",
      fibreLeaderCommsReport: "api/commissions/fibre-leader-comms-report",
      userCount: "api/commissions/user-count",
      users: "api/commissions/users",
      advances: "api/commissions/advances",
      dealerStructure: "api/commissions/user-structure",
      dealerContracts: "api/commissions/user-contracts",
      // TODO: agentTiers: "api/commissions/agent-tiers"

      // Freeze control (independent process)
      commissionFreezeStatus: "api/commissions/commission-freeze-status",
      toggleCommissionFreeze: "api/commissions/toggle-commission-freeze",

      // Import processes
      importCommissions: "api/commissions/import-commissions",

      // Post-processing
      runPostImport: "api/imports/run-post-import",
      checkPostImportStatus: "api/imports/post-import-complete",

      // Structure processing
      updateStructureLevels: "api/structure/update-structure-levels",
    };

    // Keep track of ongoing operations
    this.activeOperations = {
      exports: 0,
      imports: 0,
      postImport: false,
    };
  }

  /**
   * Export fibre commissions report
   * @param {string} cutoffDate - The cutoff date for the report
   * @returns {Promise<Blob>} CSV data as a Blob
   */
  async exportFibreCommsReport(cutoffDate) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.fibreCommsReport}?cutoff_date=${cutoffDate}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to export fibre commissions report"
        );
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(
          data.error || "Failed to export fibre commissions report"
        );
      }

      // Convert data to CSV
      return this.convertToCsv(data.data, data.filename);
    } catch (error) {
      console.error("Error exporting fibre commissions:", error);
      throw error;
    }
  }

  /**
   * Export fibre leader commissions report
   * @param {string} cutoffDate - The cutoff date for the report
   * @param {Function} progressCallback - Callback function to report progress (0-100)
   * @returns {Promise<Blob>} CSV data as a Blob
   */

  async exportFibreLeaderCommsReport(cutoffDate) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.fibreLeaderCommsReport}?cutoff_date=${cutoffDate}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to export fibre leader commissions report"
        );
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(
          data.error || "Failed to export fibre leader commissions report"
        );
      }

      // Convert data to CSV
      return this.convertToCsv(data.data, data.filename);
    } catch (error) {
      console.error("Error exporting fibre leader commissions:", error);
      throw error;
    }
  }

  /**
   * Get total user count
   * @returns {Promise<number>} Total user count
   */
  async getUserCount() {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.userCount}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get user count");
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to get user count");
      }

      return data.count;
    } catch (error) {
      console.error("Error getting user count:", error);
      throw error;
    }
  }

  /**
   * Get a page of users
   * @param {number} page - Page number (0-based)
   * @param {number} pageSize - Number of records per page
   * @returns {Promise<object>} Page of user data with pagination info
   */
  async getUsers(page, pageSize) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.users}?page=${page}&pageSize=${pageSize}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to get users");
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to get users");
      }

      return {
        data: data.data,
        totalCount: data.totalCount,
        totalPages: data.totalPages,
      };
    } catch (error) {
      console.error("Error getting users:", error);
      throw error;
    }
  }

  /**
   * Export all users with pagination
   * @param {number} pageSize - Number of records per page
   * @param {Function} progressCallback - Callback function to report progress (0-100)
   * @returns {Promise<Blob>} CSV data as a Blob
   */
  async exportAllUsers(pageSize = 1000, progressCallback = () => {}) {
    try {
      // Get total count first
      const totalCount = await this.getUserCount();
      const totalPages = Math.ceil(totalCount / pageSize);
      let combinedData = [];

      for (let page = 0; page < totalPages; page++) {
        const { data } = await this.getUsers(page, pageSize);
        combinedData = [...combinedData, ...data];

        // Report progress
        const progress = Math.round(((page + 1) / totalPages) * 100);
        progressCallback(progress);
      }

      return this.convertToCsv(combinedData, "users_master");
    } catch (error) {
      console.error("Error exporting all users:", error);
      throw error;
    }
  }

  /**
   * Calculate and export agent tiers
   * @param {string} cutoffDate - The cutoff date for tier calculation
   * @returns {Promise<Blob>} CSV data as a Blob
   */
  async exportAgentTiers(cutoffDate) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.agentTiers}?cutoff_date=${cutoffDate}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to export agent tiers");
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to export agent tiers");
      }

      return this.convertToCsv(data.data, data.filename);
    } catch (error) {
      console.error("Error exporting agent tiers:", error);
      throw error;
    }
  }

  /**
   * Export advances data
   * @returns {Promise<Blob>} CSV data as a Blob
   */
  async exportAdvances() {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.advances}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to export advances");
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to export advances");
      }

      return this.convertToCsv(data.data, data.filename);
    } catch (error) {
      console.error("Error exporting advances:", error);
      throw error;
    }
  }

  /**
   * Export dealer structure data
   * @returns {Promise<Blob>} CSV data as a Blob
   */
  async exportStructure() {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.dealerStructure}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to export dealer structure");
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to export dealer structure");
      }

      return this.convertToCsv(data.data, data.filename);
    } catch (error) {
      console.error("Error exporting dealer structure:", error);
      throw error;
    }
  }

  /**
   * Export dealer contracts data
   * @returns {Promise<Blob>} CSV data as a Blob
   */
  async exportDealerContracts() {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.dealerContracts}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to export dealer contracts");
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to export dealer contracts");
      }

      return this.convertToCsv(data.data, data.filename);
    } catch (error) {
      console.error("Error exporting dealer contracts:", error);
      throw error;
    }
  }

  /**
   * Run post import script
   * @returns {Promise<object>} Status object
   */
  async runPostImportScript() {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.runPostImport}?supplier_id=all`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to run post import script");
      }

      const data = await response.json();
      return {
        success: true,
        jobId: data.jobId,
        message: data.message,
      };
    } catch (error) {
      console.error("Error running post import script:", error);
      return {
        success: false,
        error: error.message || "Failed to run post import script",
      };
    }
  }

  /**
   * Check post import status
   * @returns {Promise<object>} Status object
   */
  async checkPostImportStatus() {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.checkPostImportStatus}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to check post import status"
        );
      }

      const data = await response.json();
      return {
        complete: data.complete,
        message: data.message,
        activeJobs: data.activeJobs || [],
      };
    } catch (error) {
      console.error("Error checking post import status:", error);
      return {
        complete: false,
        message: `Error checking status: ${error.message}`,
      };
    }
  }

  /**
   * Get commission freeze status
   * @returns {Promise<boolean>} Current freeze status
   */
  async getCommissionFreezeStatus() {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.commissionFreezeStatus}`,
        { method: "GET" }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to get commission freeze status"
        );
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to get commission freeze status");
      }

      return data.updateFreeze;
    } catch (error) {
      console.error("Error getting commission freeze status:", error);
      throw error;
    }
  }

  /**
   * Toggle commission freeze status
   * @returns {Promise<object>} New status object
   */
  async toggleCommissionFreeze() {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.toggleCommissionFreeze}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to toggle commission freeze"
        );
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.error || "Failed to toggle commission freeze");
      }

      return {
        success: true,
        updateFreeze: data.updateFreeze,
        message: data.message,
      };
    } catch (error) {
      console.error("Error toggling commission freeze:", error);
      return {
        success: false,
        error: error.message || "Failed to toggle commission freeze",
      };
    }
  }

  async updateStructureLevels(cutoffDate) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.updateStructureLevels}?cutoff_date=${cutoffDate}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || "Failed to update structure levels."
        );
      }

      return {
        success: true,
      };
    } catch (error) {
      return {
        success: false,
        error: error.message || "Failed to update structure levels.",
      };
    }
  }

  /**
   * Import commission data
   * @param {object} data - Import data
   * @returns {Promise<object>} Result object
   */
  async importCommissions(data) {
    try {
      const response = await fetch(
        `${this.baseURL}${this.endpoints.importCommissions}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to import commissions");
      }

      const responseData = await response.json();
      if (!responseData.success) {
        throw new Error(responseData.error || "Failed to import commissions");
      }

      return {
        success: true,
        message: responseData.message,
      };
    } catch (error) {
      console.error("Error importing commissions:", error);
      return {
        success: false,
        error: error.message || "Failed to import commissions",
      };
    }
  }

  /**
   * Convert JSON data to a CSV Blob
   * @param {Array} data - JSON data to convert to CSV
   * @returns {Blob} CSV data as a Blob
   */
  convertToCsv(data) {
    if (!data || !data.length) {
      return null;
    }

    // Get headers
    const headers = Object.keys(data[0]);

    // Create CSV content
    const csvRows = [
      headers.join(","), // Header row
      ...data.map(row =>
        headers
          .map(header => {
            // Handle values that need quoting (contain commas, quotes, or newlines)
            const value = row[header] != null ? String(row[header]) : "";
            if (
              value.includes(",") ||
              value.includes('"') ||
              value.includes("\n")
            ) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value;
          })
          .join(",")
      ),
    ];

    // Create blob and download
    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    return blob;
  }

  /**
   * Download a Blob as a file
   * @param {Blob} blob - The blob to download
   * @param {string} filename - File name
   */
  downloadBlob(blob, filename) {
    if (!blob) {
      console.error("No data to download");
      return;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 100);
  }
}

// Create and export a default instance
const commissionService = new CommissionService();
export default commissionService;

// Also export the class for cases where a custom instance is needed
export { CommissionService };

// Export the downloadBlob function separately for convenience

/**
 * Downloads a Blob as a file.
 * @param {Blob} blob - The blob to download
 * @param {string} filename - File name
 */
export function downloadBlob(blob, filename) {
  return commissionService.downloadBlob(blob, filename);
}
